import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';

import { MuiThemeProvider, createMuiTheme } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import { CardText } from 'material-ui/Card';
import Select from 'components/ui/SelectField';
import TextField from 'material-ui/TextField';

import FlatButton from 'material-ui/FlatButton';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import ConfirmationDialog from 'components/shared/ConfirmationDialog';
import { constants } from 'components/byesign/ByesignHelper';

import { AccentColor } from 'helpers/AccentColorHelper';
import { muiThemeNext } from 'helpers/MUIFiles';
import { getTranslatedString } from 'helpers/i18n';

import * as landlordApplicationsActions from 'actions/landlordApplicationsActions';
import { openDocument } from 'actions/landlordAPIDashboardActions';

import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import { featureEnabled } from 'helpers/ToggleBot';

const DenyApplicationSchema = Yup.object().shape({
  denyApplicationReason: Yup.string()
    .min(16, 'Reason for denying the application must be at least 16 characters.')
    .max(100, 'Reason for denying the application must be less than 100 characters.')
    .required('Enter reason for denying the application.'),
});

const theme = createMuiTheme({
  typography: {
    fontFamily: '"Circular", sans-serif',
    useNextVariants: true,
  },
});

const selectPropertyCancellationReasons = (properties, propertyId, leaseDetails) => {
  if (!properties) return {};
  let property = properties.find((property) => property.value === propertyId);
  if (!property) {
    property = leaseDetails.property;
  }

  return property.lease_cancellation_reasons;
};

class OptionDetails extends Component {
  render() {
    const style = {
      receipt_header: {
        margin: '0 0 10px 0',
        backgroundColor: '#f1f1f1',
        padding: '5px 10px',
        fontSize: '14px',
        fontWeight: '800',
        borderRadius: '3px',
      },
      transaction_card: {
        color: '#424242 !important',
        fontFamily: '"Circular", sans-serif !important',
        height: '100%',
        overflow: 'hidden',
        borderRadius: '8px',
        padding: '10px 0',
      },
    };

    return (
      <div className="row" style={{ marginTop: '10px' }}>
        <MuiThemeProvider theme={muiThemeNext(AccentColor())}>
          <Card style={style.transaction_card}>
            <CardText style={{ padding: '10px 25px' }}>
              <h3 style={{ textAlign: 'center', marginTop: '0px' }}>{this.props.optionsTitle}</h3>
              <p style={{ textAlign: 'justify' }}>{this.props.optionDetails}</p>
              {this.props.buttonVerbiage && (
                <div className="row" style={{ textAlign: 'center' }}>
                  <Button
                    aria-haspopup="true"
                    style={{
                      margin: '10px auto 0px',
                      backgroundColor: this.props.disabled ? 'grey' : AccentColor(),
                      color: 'white',
                      fontSize: '14px',
                    }}
                    onClick={this.props.optionTrigger}
                    disabled={this.props.disabled}
                  >
                    {this.props.buttonVerbiage}
                  </Button>
                </div>
              )}
            </CardText>
          </Card>
        </MuiThemeProvider>
      </div>
    );
  }
}

export default connect(
  (store) => ({
    application: store.landlordApplications.application,
    leaseDetails: store.leaseDetails.leaseDetails,
    properties: store.landlordProperties.properties,
  }),
  {
    ...landlordApplicationsActions,
    openDocument,
  }
)(
  class ApplicationOptions extends Component {
    state = {
      showCancellationDialog: false,
      cancellationReason: this.props.application.cancellation_reason || '',
      reasonRequiredError: false,
      denyApplicationReason: '',
      showDenyReasonDialog: false,
      shouldNotify: false,
    };

    toggleDocumentUploaderDialog = () => {
      this.props.toggleDocumentUploaderDialogOpen(!this.props.documentUploaderDialogOpen);
    };

    handleClose = () => {
      this.props.closePaymentPreference();
    };

    openPrePaymentsForm = () => {
      this.props.getPrePayments(this.props.leaseDetails.id);
      this.props.openPrePayments();
    };

    openManualRenewalCreator = () => {
      this.props.openManualRenewalCreator(this.props.leaseDetails.id);
    };

    modifyLease = async () => {
      this.props.saveApplicationId(this.props.application.id);
      await this.props.getLease(this.props.leaseDetails.id);
      browserHistory.push('/companies/new_lease');
    };

    openLeaseDocuments = () => {
      this.props.openLeaseDocuments();
    };

    closeDocuments = () => {
      this.props.closeDocuments();
    };

    cancelLeaseTermination = () => {
      this.props.cancelLeaseTermination(this.props.leaseDetails.id).then(() => {
        this.props.getRentRollData();
      });
    };

    remindApplicants = () => {
      this.props.remindApplicants(this.props.application.id);
    };

    viewMostRecentDocuments = () => {
      this.props.openDocument(constants.PRODUCTION, this.props.leaseDetails.document_uuid);
    };

    denyApplication = async (values) => {
      await this.props.denyApplication({
        application_id: this.props.application.id,
        send_denied_email: values.shouldNotify,
        deny_application_reason: values.denyApplicationReason,
      });
      this.props.getApplicationsAndClearOffset();
      this.props.getApplication(this.props.application.id);
      this.setState({ showDenyReasonDialog: false });
    };

    toggleDialog = (shouldNotify) => {
      this.setState({
        showDenyReasonDialog: !this.state.showDenyReasonDialog,
        shouldNotify: shouldNotify,
      });
    };

    cancelApplication = async (showCancellationDialog) => {
      this.setState({ reasonRequiredError: false });
      if (showCancellationDialog) {
        this.setState((prevState) => ({
          showCancellationDialog: !prevState.showCancellationDialog,
        }));
        return;
      }

      if (!this.state.cancellationReason && this.props.application.status !== 'cancelled') {
        this.setState({ reasonRequiredError: true });
        return;
      }

      await this.props.denyApplication({
        application_id: this.props.application.id,
        cancel_application: true,
        cancellation_reason: this.state.cancellationReason,
        uncancel_application: this.props.application.status === 'cancelled',
      });

      this.props.getApplicationsAndClearOffset();
      this.props.getApplication(this.props.application.id);
      this.setState({ showCancellationDialog: false });
    };

    cancellationOnChange = (newValue) => {
      if (!newValue) {
        return;
      }

      this.setState({ cancellationReason: newValue });
    };

    render() {
      const status = this.props.application.status;
      const propertyId = this.props.applicationDetails.property_id;
      const unitIsRentable = this.props.applicationDetails.unit_is_rentable;
      const yardiStatus = this.props.applicationDetails.yardi_status;
      const isYardi = featureEnabled('ui.applications.yardi_specific_undenial_logic');
      const showCancelOrDeny = yardiStatus === 'Applicant';
      const isModifiedLease = this.props.applicationDetails.is_modified_lease;

      const cancellationReasons = selectPropertyCancellationReasons(
        this.props.properties,
        propertyId,
        this.props.leaseDetails
      );

      return (
        <Fragment>
          {status === 'application_denied' &&
            (!isYardi || (unitIsRentable && yardiStatus === 'Denied')) && (
              <OptionDetails
                optionsTitle="Undeny Application"
                optionDetails="If you would like to revert this denied application, you can do so by clicking below."
                optionTrigger={() => this.denyApplication(false)}
                buttonVerbiage="Undeny Application"
              />
            )}
          {['cancelled', 'application_denied'].includes(status) && !unitIsRentable && isYardi && (
            <OptionDetails
              optionsTitle="Unit is Occupied"
              optionDetails="The unit this application was originally for is no longer available in Yardi. To continue with this application, ensure this unit is Rent Ready and Vacant or On Notice."
            />
          )}
          {status === 'cancelled' && unitIsRentable && yardiStatus !== 'Canceled' && isYardi && (
            <OptionDetails
              optionsTitle="We're out of sync with Yardi"
              optionDetails={`It looks like this application was modified manually to ${yardiStatus} in Yardi. In order to uncancel this application, ensure the status of this application is set to "Cancelled" in Yardi`}
            />
          )}
          {status === 'application_denied' &&
            unitIsRentable &&
            yardiStatus !== 'Denied' &&
            isYardi && (
              <OptionDetails
                optionsTitle="We're out of sync with Yardi"
                optionDetails={`It looks like this application was modified manually to ${yardiStatus} in Yardi. In order to undeny this application, ensure the status of this application is set to "Denied" in Yardi`}
              />
            )}
          {status === 'cancelled' &&
            (!isYardi || (unitIsRentable && yardiStatus === 'Canceled')) && (
              <OptionDetails
                optionsTitle="Uncancel Application"
                optionDetails="If you would like to revert this cancelled application, you can do so by clicking below."
                optionTrigger={() => this.cancelApplication()}
                buttonVerbiage="Uncancel Application"
              />
            )}
          <ModifyLeaseOption
            status={status}
            modifyLease={this.modifyLease}
            isYardi={isYardi}
            showCancelOrDeny={showCancelOrDeny}
            isModifiedLease={isModifiedLease}
            canCreateLease={this.props.applicationDetails.can_create_application}
            yardiStatus={yardiStatus}
          />
          {status !== 'application_denied' &&
            status !== 'cancelled' &&
            status !== 'payment_received' && (
              <Fragment>
                {isYardi && !showCancelOrDeny && yardiStatus === 'Applicant' && (
                  <OptionDetails
                    optionsTitle="Transfer Application"
                    optionDetails="If you would like to transfer this application to another unit, you can do so by clicking below."
                    optionTrigger={this.props.toggleApplication}
                    buttonVerbiage="Transfer Application"
                  />
                )}
                {!['leased_to_other_applicant', 'create_lease'].includes(status) && (
                  <OptionDetails
                    optionsTitle="Remind Applicants"
                    optionDetails="If you would like to remind the applicants, you can do so by clicking below."
                    optionTrigger={this.remindApplicants}
                    buttonVerbiage="Remind Applicants"
                  />
                )}
                <OptionDetails
                  optionsTitle="Cancel Application"
                  optionDetails="Application cancellations are performed if the lead is no longer interested. If you would like to cancel the application, you can do so by clicking below. No email notification will be sent."
                  optionTrigger={() => this.cancelApplication(true)}
                  buttonVerbiage="Cancel Application"
                />
                {(!isYardi || (isYardi && showCancelOrDeny)) && (
                  <Fragment>
                    <OptionDetails
                      optionsTitle="Deny Application"
                      optionDetails="If you would like to deny the application, you can do so by clicking below. No email notification will be sent."
                      optionTrigger={() => this.toggleDialog(false)}
                      buttonVerbiage="Deny Application"
                    />
                    <OptionDetails
                      optionsTitle="Deny Application With Notice"
                      optionDetails="If you would like to deny the application, and send an email notification to all applicants, you can do so by clicking below."
                      optionTrigger={() => this.toggleDialog(true)}
                      buttonVerbiage="Deny Application"
                    />
                    <OptionDetails
                      optionsTitle="Transfer Application"
                      optionDetails="If you would like to modify this application, you can do so by clicking below."
                      optionTrigger={this.props.toggleApplication}
                      buttonVerbiage="Transfer Application"
                    />
                  </Fragment>
                )}
                {this._renderDenyApplicationReasonForm()}
              </Fragment>
            )}
          {this.props.leaseDetails.document_uuid && (
            <OptionDetails
              optionsTitle="View Most Recent Document"
              optionDetails="If you would like to view the most recent lease document related to this application, you can do so by
              clicking below."
              optionTrigger={this.viewMostRecentDocuments}
              buttonVerbiage="View Document"
            />
          )}
          {this.props.leaseDetails.lease_file_url && (
            <OptionDetails
              optionsTitle={getTranslatedString('landlordPortal.leaseOptions.viewRecentDocument')}
              optionDetails={getTranslatedString(
                'landlordPortal.leaseOptions.viewRecentDocumentDetails'
              )}
              optionTrigger={() => window.open(this.props.leaseDetails.lease_file_url, '_blank')}
              buttonVerbiage={getTranslatedString('landlordPortal.leaseOptions.viewDocument')}
            />
          )}
          <ConfirmationDialog
            showDialog={this.state.showCancellationDialog}
            hideDialog={this.cancelApplication}
            userConfirmVerbiage="Cancel Application"
            userConfirmAction={this.cancelApplication}
            dialogTitle="Cancel Application"
            dialogBodyVerbiage={this._renderCancelApplication(
              cancellationReasons,
              this.state.cancellationReason
            )}
          />
        </Fragment>
      );
    }

    _renderCancelApplication = (cancellationReasons, cancellationReason) => (
      <Fragment>
        <p>Are you sure you want to cancel this application? This cannot be undone.</p>
        <div className="row">
          <Select
            size="large"
            value={cancellationReason}
            onChange={this.cancellationOnChange}
            style={{ textAlign: 'left', fontSize: '14px', width: '100%' }}
          >
            {/* DO NOT TRANSFORM THE CANCELLATION REASONS AS IT WILL IMPACT THIRD PARTY INTEGRATIONS */}
            {cancellationReasons.map((cancellationReason) => {
              return (
                <Select.Option key={cancellationReason} value={cancellationReason}>
                  {cancellationReason}
                </Select.Option>
              );
            })}
          </Select>
        </div>
      </Fragment>
    );

    _renderDenyApplicationReasonForm = () => {
      return (
        <MuiThemeProvider theme={theme}>
          <Dialog disableBackdropClick open={this.state.showDenyReasonDialog} fullWidth>
            <DialogTitle style={{ fontFamily: 'Circular-Black' }}>Reason for Declining</DialogTitle>
            <DialogContent>
              <DialogContentText>Enter a reason for denying this application.</DialogContentText>
              <Formik
                initialValues={{
                  denyApplicationReason: '',
                  shouldNotify: this.state.shouldNotify,
                }}
                validationSchema={DenyApplicationSchema}
                onSubmit={(values) => this.denyApplication(values)}
              >
                {({ values, errors, touched, handleChange }) => (
                  <Form>
                    <TextField
                      name="denyApplicationReason"
                      autoFocus
                      margin="dense"
                      id="deny_application_reason"
                      label="Reason for cancellation"
                      type="text"
                      fullWidth
                      multiLine
                      variant="outline"
                      value={values.denyApplicationReason}
                      onChange={handleChange}
                    />
                    {errors.denyApplicationReason && touched.denyApplicationReason ? (
                      <div style={{ color: '#ff3333' }}>{errors.denyApplicationReason}</div>
                    ) : null}

                    <DialogActions style={{ justifyContent: 'space-between' }}>
                      <FlatButton label="Close" primary={true} onClick={this.toggleDialog} />
                      <FlatButton label="Decline" primary={true} type="submit" />
                    </DialogActions>
                  </Form>
                )}
              </Formik>
            </DialogContent>
          </Dialog>
        </MuiThemeProvider>
      );
    };
  }
);

const ModifyLeaseOption = ({
  status,
  modifyLease,
  isYardi,
  showCancelOrDeny,
  isModifiedLease,
  canCreateLease,
}) => {
  const statusCheck = [
    'pending_tenant_signature',
    'pending_landlord_signature',
    'payment_received',
    'pending_payment',
  ].includes(status);
  if (
    !statusCheck ||
    ((status === 'payment_received' || status === 'pending_payment') &&
      !featureEnabled('ui.applications_page.modify_lease_when_payment_received'))
  ) {
    return null;
  }

  if (isYardi && !showCancelOrDeny && isModifiedLease) {
    return (
      <OptionDetails
        optionsTitle="Modify Lease"
        optionDetails="This lease has been completed and synced to Yardi. Once a lease has been synced to Yardi, any modifications made to it in Yuhu will appear in Yuhu only. A pdf with the modified details will be uploaded to Yardi."
        optionTrigger={modifyLease}
        buttonVerbiage="Modify Lease"
        disabled={!canCreateLease}
      />
    );
  }

  return (
    <OptionDetails
      optionsTitle="Modify Lease"
      optionDetails="If you would like to modify the lease document (or change the way you sign the lease), you can do so by clicking below.
Note: All applicants will be asked to resign the document."
      optionTrigger={modifyLease}
      buttonVerbiage="Modify Lease"
      disabled={!canCreateLease}
    />
  );
};
